/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/no-unescaped-entities */
// ════════════════════════════════════════════════════════════════
// Palmy (Time off) — dedicated marketing-page sections.
// A fuller redesign of /vacation-tracker on the BuddiesHR design
// system (see styles/bh-tokens.css). Rendered inside <main className="bh">.
// Reuses the shared AddToSlackButton for the final tracked CTA; the rest
// is bh-styled inline like the other migrated marketing pages
// (components/clappy/pageSections.js, components/billy/pageSections.js).
// ════════════════════════════════════════════════════════════════
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import AddToSlackButton from '@buddieshr/components/addToSlackButton';
import AppsCrossSell from '@buddieshr/components/appsCrossSell';
import { LOVE } from '@buddieshr/utils/constants';

// Palmy accent + pastel, from the app palette (see bh-tokens.css).
// Kept as raw hex so we can append alpha (e.g. ACCENT + '33').
const ACCENT = '#E59451';
const APP_BG = '#FBE3CE';
const LOGO = '/img/apps/palmy/logo.png';

// ── Content ─────────────────────────────────────────────────────
const FEATURES = [
  {
    t: 'Vacation requests',
    screen: 'request',
    d: 'Request and manage vacation days directly in Slack or Teams.',
  },
  {
    t: 'One-click approvals',
    screen: 'approve',
    d: 'Managers approve or reject vacation requests with a single click, directly in Slack or Teams.',
  },
  {
    t: 'Quota and accruals tracking',
    screen: 'quota',
    d: "Define your team's vacation quota and track accruals over time.",
  },
  {
    t: 'Google Calendar & Outlook sync',
    screen: 'gcal',
    d: 'Sync vacation requests to your company-wide Google Calendar or Outlook.',
  },
  {
    t: 'Status automatic sync',
    screen: 'status',
    d: "Slack or Teams status flips to 🌴 automatically while you're out.",
  },
  {
    t: 'Manage vacation types',
    screen: 'leavetypes',
    d: "Personalize vacation types to fit your team's needs.",
  },
  {
    t: 'Policies per team',
    screen: 'policies',
    d: 'Unlimited, accrued, regional. Mix and match.',
  },
  {
    t: 'History logs',
    screen: 'history',
    d: 'Keep track of all vacation requests and approvals for audit and reporting.',
  },
];

const SHINE = [
  {
    k: 'Stop wasting time and energy managing vacation requests',
    icon: '⏱️',
    d: 'Say goodbye to spreadsheets and endless emails and chat messages.',
  },
  {
    k: 'Stay focused, do it all in Slack or Teams',
    icon: '🎯',
    d: 'Logging into another app to log requests is just too much pain. By making it easily accessible in Slack or Teams, we ensure efficiency for HRs, managers and employees.',
  },
  {
    k: 'Keep vacations transparent and fair',
    icon: '⚖️',
    d: "Palmy makes sure everyone gets a fair and transparent vacation policy. It's also a good way to make sure no errors are made.",
  },
];

const CHECKLIST = [
  'Setup in 3 minutes',
  'Works in Slack, Teams and the web',
  'Web dashboard for the full view',
];

// ════════════════════════════════════════════════════════════════
// HERO VIZ — team time-off calendar, a manager approval card and a
// daily "who's off" reminder, with the mascot floating above.
// ════════════════════════════════════════════════════════════════
function PalmyHeroViz() {
  return (
    <div className="palmy-viz-outer" style={{ position: 'relative', width: 520, height: 540, maxWidth: '100%' }}>
      <style>{`
        @keyframes palmy-up { 0% { transform: translateY(16px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes palmy-in { 0% { transform: translateY(-12px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes palmy-bob { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-10px) rotate(2deg); } }
        @keyframes palmy-mascot-pop { 0% { transform: scale(.9) rotate(-6deg); opacity: 0; } 55% { transform: scale(1.05) rotate(4deg); opacity: 1; } 100% { transform: scale(1) rotate(0); opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .palmy-viz * { animation: none !important; opacity: 1 !important; } }
        @media (max-width: 1024px) { .palmy-viz-outer { width: 440px !important; height: 458px !important; } .palmy-viz { transform: scale(0.846); } }
        @media (max-width: 600px)  { .palmy-viz-outer { width: 340px !important; height: 354px !important; } .palmy-viz { transform: scale(0.654); } }
        @media (max-width: 380px)  { .palmy-viz-outer { width: 290px !important; height: 302px !important; } .palmy-viz { transform: scale(0.558); } }
      `}</style>
      <div className="palmy-viz" style={{ position: 'relative', width: 520, height: 540, transformOrigin: 'top left' }}>
        {/* mascot, small, top-right */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 6,
            width: 112,
            height: 112,
            filter: 'drop-shadow(0 12px 28px rgba(31,27,22,.16))',
            animation: 'palmy-mascot-pop 1.1s cubic-bezier(.3,1.5,.4,1) both',
          }}
        >
          <div style={{ width: '100%', height: '100%', animation: 'palmy-bob 5s ease-in-out 1.1s infinite' }}>
            <img src={LOGO} alt="Palmy" style={{ width: '100%', height: '100%' }} />
          </div>
        </div>

        {/* team time-off calendar — top */}
        <div
          style={{
            position: 'absolute',
            top: 30,
            left: 0,
            width: 350,
            background: '#fff',
            border: '1px solid var(--bh-line)',
            borderRadius: 18,
            boxShadow: '0 16px 40px rgba(31,27,22,.12)',
            padding: '18px 20px 20px',
            animation: 'palmy-in 1s cubic-bezier(.3,1.3,.4,1) .3s both',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: ACCENT }}>
              #team-design · who&apos;s off
            </div>
            <div style={{ fontSize: 11, color: '#9A938A' }}>Apr 21–25</div>
          </div>
          {(() => {
            const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
            const rows = [
              ['Maya Kapoor', '#7C6FF0', [0, 0, 1, 1, 1]],
              ['Jordan Lee', '#4FB36A', [1, 1, 0, 0, 0]],
              ['You', ACCENT, [0, 0, 0, 1, 1]],
            ];
            return (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '92px repeat(5,1fr)', gap: 5, marginBottom: 7 }}>
                  <span />
                  {days.map((d, i) => (
                    <span key={i} style={{ fontSize: 9.5, fontWeight: 700, color: '#9A938A', textAlign: 'center' }}>
                      {d}
                    </span>
                  ))}
                </div>
                {rows.map(([who, ac, cells], r) => (
                  <div key={r} style={{ display: 'grid', gridTemplateColumns: '92px repeat(5,1fr)', gap: 5, marginBottom: 6, alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 18, height: 18, borderRadius: '50%', background: ac, color: '#fff', fontSize: 8.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {who[0]}
                      </span>
                      <span style={{ fontSize: 11, color: '#3A342C', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {who}
                      </span>
                    </span>
                    {cells.map((on, i) => (
                      <span key={i} style={{ height: 18, borderRadius: 5, background: on ? ACCENT : '#F1EFEA' }} />
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* approval request card — manager view */}
        <div
          style={{
            position: 'absolute',
            bottom: 148,
            left: 30,
            width: 372,
            background: '#fff',
            borderRadius: 18,
            boxShadow: '0 18px 44px rgba(31,27,22,.18)',
            border: '1px solid rgba(0,0,0,.04)',
            overflow: 'hidden',
            animation: 'palmy-up 1s cubic-bezier(.3,1.3,.4,1) .7s both',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: '1px solid var(--bh-line)', background: '#FBFAF8' }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, background: ACCENT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🌴</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1F1B16' }}>Palmy</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9A938A' }}>manager view</span>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ width: 30, height: 30, borderRadius: '50%', background: '#7C6FF0', color: '#fff', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>S</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1F1B16' }}>Sam requested time off</div>
                <div style={{ fontSize: 12, color: '#5a5148' }}>Thu Apr 24 – Fri Apr 25 · 2 days · Vacation</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ flex: 1, textAlign: 'center', background: '#4FB36A', color: '#fff', fontSize: 12.5, fontWeight: 800, padding: '8px 0', borderRadius: 8 }}>✓ Approve</span>
              <span style={{ flex: 1, textAlign: 'center', background: '#fff', color: '#B6402F', border: '1.5px solid #E3B3AA', fontSize: 12.5, fontWeight: 800, padding: '8px 0', borderRadius: 8 }}>✕ Reject</span>
            </div>
          </div>
        </div>

        {/* daily who's-off reminder — bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 110,
            width: 372,
            background: '#fff',
            borderRadius: 18,
            boxShadow: '0 18px 44px rgba(31,27,22,.2)',
            border: '1px solid rgba(0,0,0,.04)',
            overflow: 'hidden',
            animation: 'palmy-up 1s cubic-bezier(.3,1.3,.4,1) 1.1s both',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: '1px solid var(--bh-line)', background: '#FBFAF8' }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, background: ACCENT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🌴</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1F1B16' }}>Palmy</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9A938A' }}>#team-design · 9:00 AM</span>
          </div>
          <div style={{ padding: '13px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1F1B16', marginBottom: 9 }}>🌴 Off today</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                ['M', '#7C6FF0', 'Maya Kapoor', 'back Thursday'],
                ['J', '#4FB36A', 'Jordan Lee', 'back Monday'],
              ].map(([ini, ac, n, note]) => (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: ac, color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {ini}
                  </span>
                  <span style={{ fontWeight: 700, color: '#1F1B16' }}>{n}</span>
                  <span style={{ color: '#9A938A' }}>· {note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════════════════════════
export function PalmyHero() {
  return (
    <section
      className="bh-hero-section"
      style={{
        background: APP_BG,
        position: 'relative',
        overflow: 'hidden',
        borderBottom: `1px solid ${ACCENT}22`,
        padding: '70px 32px 56px',
      }}
    >
      <div
        className="bh-hero-grid"
        style={{
          maxWidth: 1240,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: 56,
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
            <Link href="/" style={{ fontSize: 13, color: 'var(--bh-ink-mute)', textDecoration: 'none' }}>
              BuddiesHR
            </Link>
            <span style={{ fontSize: 13, color: 'var(--bh-ink-mute)' }}>/</span>
            <span style={{ fontSize: 13, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700 }}>
              Time off
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
            <div className="bh-float" style={{ width: 72, height: 72, flexShrink: 0 }}>
              <Image src={LOGO} alt="Palmy logo" width={72} height={72} />
            </div>
            <div>
              <div style={{ fontSize: 13, color: ACCENT, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                Palmy · Vacation &amp; PTO tracker
              </div>
              <div style={{ fontSize: 16, color: 'var(--bh-ink-soft)' }}>Time off for Slack + Teams</div>
            </div>
          </div>

          <h1
            style={{
              fontFamily: 'var(--bh-font-display)',
              fontWeight: 500,
              fontSize: 'clamp(44px, 6vw, 78px)',
              lineHeight: 0.98,
              letterSpacing: '-0.035em',
              margin: '0 0 20px',
              color: 'var(--bh-ink)',
            }}
          >
            Time off on <span style={{ fontStyle: 'italic', color: ACCENT }}>autopilot</span>.
          </h1>

          <p style={{ fontSize: 19, color: 'var(--bh-ink-soft)', lineHeight: 1.5, maxWidth: 560, marginBottom: 28 }}>
            Employees request days off directly in Slack or Teams. Managers approve or reject right there too. The team
            calendar and statuses sync automatically.
          </p>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link href="/get-started" className="bh-btn primary">
              <img src="/img/redesign/slack-logo.png" alt="" style={{ width: 20, height: 20, marginRight: 8, verticalAlign: '-4px' }} />
              Start free in Slack <span className="bh-arrow">→</span>
            </Link>
            <Link href="/get-started" className="bh-btn ghost">
              <img src="/img/redesign/teams-logo.png" alt="" style={{ width: 20, height: 20, marginRight: 8, verticalAlign: '-4px' }} />
              Start free in Teams <span className="bh-arrow">→</span>
            </Link>
          </div>

          <div style={{ marginTop: 16, fontSize: 13, color: 'var(--bh-ink-mute)' }}>
            30-day free trial · Free forever under 10 employees · From $1/user/month
          </div>

          <div style={{ marginTop: 8, fontSize: 13.5 }}>
            <Link
              href="/demo"
              style={{
                color: 'var(--bh-ink-soft)',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(31,27,22,.25)',
                textUnderlineOffset: 3,
                fontWeight: 500,
              }}
            >
              Or, book a 15-min demo →
            </Link>
          </div>

          <div style={{ marginTop: 18, fontSize: 13.5, color: 'var(--bh-ink-mute)' }}>
            ⭐ 4.9 on G2 · Trusted by 1,000+ HR teams at fast-moving SMBs
          </div>
        </div>

        <div className="bh-hero-art" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <PalmyHeroViz />
        </div>
      </div>
    </section>
  );
}

// ── Faux Slack/Teams product panel for the demo section ─────────
function PlatformTag({ label, logo }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
      <img src={logo} alt="" style={{ width: 16, height: 16 }} />
      <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--bh-ink-soft)' }}>
        {label}
      </span>
    </div>
  );
}

function PalmyProductScreen() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 560, width: '100%' }}>
      {/* request + approval — Slack */}
      <div>
        <PlatformTag label="In Slack" logo="/img/redesign/slack-logo.png" />
        <div style={{ background: '#fff', border: `1.5px solid ${ACCENT}40`, borderRadius: 18, overflow: 'hidden', boxShadow: 'var(--bh-shadow-md)', fontFamily: 'Lato, -apple-system, sans-serif' }}>
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #E2E2E2', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15.5, fontWeight: 900, color: '#1D1C1D' }}># team-design</span>
            <span style={{ fontSize: 12.5, color: '#616061' }}>☆ · 24 members</span>
          </div>
          <div style={{ padding: '14px 20px 16px' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: 4, background: APP_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>🌴</div>
              <div style={{ flex: 1, minWidth: 0, marginLeft: 4 }}>
                <div style={{ fontSize: 15, lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 900, color: '#1D1C1D' }}>Palmy</span>
                  <span style={{ fontSize: 10, color: '#616061', background: '#E8E8E8', padding: '1px 4px', borderRadius: 2, marginLeft: 6, fontWeight: 700, verticalAlign: '1px' }}>APP</span>
                  <span style={{ fontSize: 12, color: '#616061', marginLeft: 6 }}>9:00 AM</span>
                </div>
                <div style={{ fontSize: 15, color: '#1D1C1D', lineHeight: 1.45 }}>sent a request to Maya Kapoor</div>
                <div style={{ marginTop: 6, display: 'flex' }}>
                  <div style={{ width: 4, borderRadius: 8, background: `${ACCENT}66`, flexShrink: 0 }} />
                  <div style={{ padding: '8px 12px', minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5, color: '#1D1C1D', marginBottom: 4 }}>Sam requested time off</div>
                    <div style={{ fontSize: 14, color: '#1D1C1D', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                      Thu Apr 24 – Fri Apr 25 · 2 days · Vacation{'\n'}4.5 days remaining after this
                    </div>
                    <div style={{ fontSize: 12, color: ACCENT, marginTop: 8, fontWeight: 700, letterSpacing: '.03em' }}>✓ APPROVE   ·   ✕ REJECT</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* daily heads-up — Microsoft Teams */}
      <div>
        <PlatformTag label="In Microsoft Teams" logo="/img/redesign/teams-logo.png" />
        <div style={{ background: '#fff', border: `1.5px solid ${ACCENT}40`, borderRadius: 18, overflow: 'hidden', boxShadow: 'var(--bh-shadow-md)', fontFamily: '"Segoe UI", -apple-system, sans-serif' }}>
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #E2E2E2', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15.5, fontWeight: 900, color: '#242424' }}>General</span>
          </div>
          <div style={{ padding: '14px 20px 16px' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: APP_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>🌴</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 900, color: '#1D1C1D' }}>Palmy</span>
                  <span style={{ fontSize: 10, color: '#fff', background: '#6264A7', padding: '1px 4px', borderRadius: 3, marginLeft: 6, fontWeight: 700, verticalAlign: '1px' }}>BOT</span>
                  <span style={{ fontSize: 12, color: '#616061', marginLeft: 6 }}>9:00 AM</span>
                </div>
                <div style={{ fontSize: 15, color: '#1D1C1D', lineHeight: 1.45 }}>daily heads-up</div>
                <div style={{ marginTop: 6, display: 'flex' }}>
                  <div style={{ width: 4, borderRadius: 8, background: '#6264A7', flexShrink: 0 }} />
                  <div style={{ padding: '8px 12px', minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5, color: '#1D1C1D', marginBottom: 4 }}>🌴 Off today</div>
                    <div style={{ fontSize: 14, color: '#1D1C1D', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                      Maya Kapoor · back Thursday{'\n'}Jordan Lee · back Monday
                    </div>
                    <div style={{ fontSize: 12, color: ACCENT, marginTop: 8, fontWeight: 700, letterSpacing: '.03em' }}>CALENDAR + STATUS SYNCED</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ background: '#F5F5F5', border: '1px solid #E1E1E1', borderRadius: 4, padding: '2px 8px', fontSize: 12, color: '#1D1C1D', fontWeight: 600 }}>🌴 3</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PRODUCT DEMO — "right where your team already is"
// ════════════════════════════════════════════════════════════════
export function PalmyProductDemo() {
  return (
    <section className="bh-section" style={{ background: 'var(--bh-cream)' }}>
      <div
        className="bh-stack"
        style={{
          maxWidth: 1240,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1.2fr',
          gap: 64,
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em', color: ACCENT, textTransform: 'uppercase', marginBottom: 12 }}>
            Live in Slack &amp; Teams
          </div>
          <h2 style={{ fontSize: 'clamp(34px, 4.4vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.02, marginBottom: 20 }}>
            Right where your team already <span style={{ fontStyle: 'italic', color: ACCENT }}>is</span>.
          </h2>
          <p style={{ fontSize: 18, color: 'var(--bh-ink-soft)', lineHeight: 1.55, marginBottom: 22 }}>
            No portal to log into. No new tab to open. Palmy handles requests, approvals and the team calendar exactly
            where your team already spends their day.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {CHECKLIST.map((x) => (
              <li key={x} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, color: 'var(--bh-ink-soft)' }}>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    background: ACCENT,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  ✓
                </span>
                {x}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <PalmyProductScreen />
        </div>
      </div>
    </section>
  );
}

// ── Per-feature mini product screens (kinds match FEATURES) ─────
function FeatureViz({ kind }) {
  const c = ACCENT;
  const bg = APP_BG;
  const frame = {
    background: '#fff',
    border: '1px solid var(--bh-line)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
    height: 132,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxShadow: '0 6px 18px rgba(31,27,22,.05)',
    overflow: 'hidden',
  };
  const toggle = (on) => (
    <span style={{ width: 30, height: 17, borderRadius: 9, background: on ? c : '#D9D4CC', position: 'relative', flexShrink: 0, display: 'inline-block' }}>
      <span style={{ position: 'absolute', top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#fff' }} />
    </span>
  );

  let body = null;
  if (kind === 'request') {
    body = (
      <div style={{ border: '1px solid var(--bh-line)', borderRadius: 8, padding: '8px 10px' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: '#9A938A', marginBottom: 7 }}>New request</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <span style={{ flex: 1, border: '1px solid var(--bh-line)', borderRadius: 6, padding: '4px 8px', fontSize: 10.5, fontWeight: 600, color: '#1F1B16' }}>Thu Apr 24 → Fri Apr 25</span>
          <span style={{ border: '1px solid var(--bh-line)', borderRadius: 6, padding: '4px 8px', fontSize: 10.5, fontWeight: 600, color: '#3A342C' }}>🌴</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, color: '#fff', background: c, padding: '4px 12px', borderRadius: 6 }}>Submit</span>
        </div>
      </div>
    );
  } else if (kind === 'approve') {
    body = (
      <div style={{ border: '1px solid var(--bh-line)', borderRadius: 8, padding: '8px 10px' }}>
        <div style={{ fontSize: 11.5, color: '#3A342C', lineHeight: 1.4, marginBottom: 8 }}>
          <b style={{ color: '#1264A3' }}>@Sam</b> · 2 days · Thu–Fri 🌴
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ flex: 1, textAlign: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff', background: '#4FB36A', padding: '5px 0', borderRadius: 6 }}>✓ Approve</span>
          <span style={{ flex: 1, textAlign: 'center', fontSize: 10.5, fontWeight: 800, color: '#B6402F', border: '1px solid #E3B3AA', padding: '5px 0', borderRadius: 6 }}>✕ Reject</span>
        </div>
      </div>
    );
  } else if (kind === 'quota') {
    body = (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 7 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1F1B16' }}>Vacation balance</span>
          <span style={{ fontSize: 11, fontWeight: 800, color: c }}>16.5 / 25 days</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: '#F4F2EE', overflow: 'hidden', marginBottom: 7 }}>
          <div style={{ width: '66%', height: '100%', borderRadius: 4, background: c }} />
        </div>
        <div style={{ fontSize: 10.5, color: '#9A938A', fontWeight: 600 }}>+2.08 days accrued monthly</div>
      </div>
    );
  } else if (kind === 'gcal') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--bh-line)', borderRadius: 8, padding: '7px 9px' }}>
          <span style={{ width: 20, height: 20, borderRadius: 5, background: '#4285F4', color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>31</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#3A342C', flex: 1 }}>Team · Time off</span>
          <span style={{ fontSize: 9.5, color: c, fontWeight: 700 }}>● synced</span>
        </div>
        <div style={{ fontSize: 11, color: '#3A342C', paddingLeft: 4 }}>
          🌴 Sam · Thu–Fri <span style={{ color: '#9A938A' }}>· added automatically</span>
        </div>
      </div>
    );
  } else if (kind === 'status') {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid var(--bh-line)', borderRadius: 10, padding: '10px 12px' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <span style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🙂</span>
          <span style={{ position: 'absolute', bottom: -4, right: -4, fontSize: 14 }}>🌴</span>
        </div>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1F1B16' }}>Maya Kapoor</div>
          <div style={{ fontSize: 11, color: c, fontWeight: 600 }}>Out until Thu · 🌴 Vacationing</div>
        </div>
      </div>
    );
  } else if (kind === 'leavetypes') {
    body = (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[['🌴', 'Vacation'], ['🤒', 'Sick'], ['🕐', 'Half-day'], ['💸', 'Comp'], ['🍼', 'Parental'], ['+', 'Custom']].map(([e, t]) => (
          <span
            key={t}
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              padding: '5px 9px',
              borderRadius: 8,
              background: t === 'Custom' ? bg : '#F4F2EE',
              color: t === 'Custom' ? c : '#3A342C',
              border: t === 'Custom' ? `1px dashed ${c}` : '1px solid var(--bh-line)',
            }}
          >
            {e} {t}
          </span>
        ))}
      </div>
    );
  } else if (kind === 'policies') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {[['Design · Unlimited', true], ['Eng · 25 days accrued', true], ['EU · statutory + 5', false]].map(([label, on]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#3A342C', fontWeight: 600 }}>{label}</span>
            {toggle(on)}
          </div>
        ))}
      </div>
    );
  } else if (kind === 'history') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[['Apr 12', 'Sam · 2d vacation', '✓'], ['Mar 28', 'Maya · 1d sick', '✓'], ['Mar 03', 'Jordan · 3d comp', '✕']].map(([d, label, st]) => (
          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
            <span style={{ fontWeight: 800, color: '#9A938A', width: 40, flexShrink: 0 }}>{d}</span>
            <span style={{ color: '#3A342C', fontWeight: 600, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            <span style={{ fontWeight: 900, color: st === '✓' ? '#4FB36A' : '#B6402F' }}>{st}</span>
          </div>
        ))}
      </div>
    );
  }
  return <div style={frame}>{body}</div>;
}

// ════════════════════════════════════════════════════════════════
// FEATURE GRID (8)
// ════════════════════════════════════════════════════════════════
export function PalmyFeatureGrid() {
  return (
    <section className="bh-section" style={{ background: 'var(--bh-cream-deep)' }} id="features">
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em', color: 'var(--bh-ink-mute)', textTransform: 'uppercase', marginBottom: 10 }}>
            Everything in Palmy
          </div>
          <h2 style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', letterSpacing: '-0.03em', lineHeight: 1, maxWidth: 760, margin: '0 auto' }}>
            Small enough to feel light.
            <br />
            <span style={{ fontStyle: 'italic', color: ACCENT }}>Deep</span> enough to rely on.
          </h2>
        </div>
        <div className="bh-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {FEATURES.map((f) => (
            <div
              key={f.t}
              style={{
                background: '#fff',
                border: '1px solid var(--bh-line)',
                borderRadius: 'var(--bh-r-lg)',
                padding: 18,
                paddingBottom: 26,
              }}
            >
              <FeatureViz kind={f.screen} />
              <div style={{ padding: '0 8px' }}>
                <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 21, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>
                  {f.t}
                </div>
                <div style={{ fontSize: 14.5, color: 'var(--bh-ink-soft)', lineHeight: 1.55 }}>{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// REVIEWS (3)
// ════════════════════════════════════════════════════════════════
// Palmy has no product-specific reviews in the shared LOVE data yet, so
// this section surfaces real, BuddiesHR-wide testimonials (people using
// the suite as a whole) rather than fabricated Palmy quotes. Curated to 3
// suite-level reviews by name, falling back to any multi-app LOVE entry if
// a name changes.
const GENERAL_REVIEW_NAMES = ['Sammie Z.', 'Melissa B.', 'Robert S.'];
const isSuiteWide = (l) => Array.isArray(l.apps) && l.apps.length >= 3;
const curatedReviews = GENERAL_REVIEW_NAMES.map((n) => LOVE.find((l) => l.name === n && isSuiteWide(l))).filter(Boolean);
const REVIEWS = (curatedReviews.length === 3 ? curatedReviews : LOVE.filter(isSuiteWide)).slice(0, 3);

const initials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2);

export function PalmyReviews() {
  return (
    <section className="bh-section" style={{ background: 'var(--bh-cream)', borderTop: '1px solid var(--bh-line)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div className="bh-chip" style={{ marginBottom: 16 }}>
            💬 ⭐ 4.9 on G2
          </div>
          <h2 style={{ fontSize: 'clamp(34px, 4.4vw, 54px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            You&rsquo;re sure to make the <span style={{ fontStyle: 'italic', color: ACCENT }}>right choice</span>.
          </h2>
          <p style={{ fontSize: 16, color: 'var(--bh-ink-mute)', marginTop: 14 }}>
            From teams running the BuddiesHR suite in Slack and Teams.
          </p>
        </div>
        <div className="bh-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {REVIEWS.map((r, i) => {
            const hasPhoto = r.profilePicture && !r.profilePicture.endsWith('user.png');
            const roleLine = [r.role, r.companyName].filter(Boolean).join(', ');
            return (
              <div
                key={i}
                style={{
                  background: '#fff',
                  borderRadius: 'var(--bh-r-lg)',
                  padding: 28,
                  border: '1.5px solid var(--bh-line)',
                  boxShadow: 'var(--bh-shadow-sm)',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: -20,
                    right: -8,
                    fontFamily: 'var(--bh-font-display)',
                    fontSize: 130,
                    color: ACCENT,
                    opacity: 0.1,
                    lineHeight: 1,
                    fontStyle: 'italic',
                  }}
                >
                  &ldquo;
                </div>
                <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <span key={j} style={{ color: 'var(--bh-sun-400)', fontSize: 16 }}>
                      ★
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 15.5, lineHeight: 1.55, color: 'var(--bh-ink)', position: 'relative', zIndex: 1 }}>{r.content}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
                  {hasPhoto ? (
                    <img src={r.profilePicture} alt={r.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: ACCENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials(r.name)}
                    </div>
                  )}
                  <div style={{ flex: 1, paddingTop: 16, borderTop: '1px solid var(--bh-line)' }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--bh-ink-mute)' }}>{roleLine}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <Link href="/reviews" className="bh-btn secondary" style={{ textDecoration: 'none' }}>
            Read more reviews <span className="bh-arrow">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// WHAT MAKES PALMY SHINE (3)
// ════════════════════════════════════════════════════════════════
export function PalmyShine() {
  return (
    <section className="bh-section" style={{ background: 'var(--bh-cream-deep)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ maxWidth: 720, marginBottom: 56 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em', color: ACCENT, textTransform: 'uppercase', marginBottom: 12 }}>
            What makes Palmy shine
          </div>
          <h2 style={{ fontSize: 'clamp(36px, 4.6vw, 56px)', letterSpacing: '-0.03em', lineHeight: 1.02 }}>
            Less admin, <span style={{ fontStyle: 'italic', color: ACCENT }}>more</span> vacation.
          </h2>
        </div>
        <div className="bh-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {SHINE.map((s) => (
            <div
              key={s.k}
              style={{
                background: '#fff',
                border: '1px solid var(--bh-line)',
                borderRadius: 'var(--bh-r-lg)',
                padding: 30,
              }}
            >
              <div style={{ width: 52, height: 52, borderRadius: 14, background: APP_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 18 }}>
                {s.icon}
              </div>
              <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 23, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>{s.k}</div>
              <div style={{ fontSize: 15, color: 'var(--bh-ink-soft)', lineHeight: 1.55 }}>{s.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// FAQ — "Before you ask." (+ FAQPage JSON-LD for SEO / AI citation)
// ════════════════════════════════════════════════════════════════
const FAQS = [
  [
    'What is Palmy?',
    'Palmy is the vacation and PTO tracker from BuddiesHR. Employees request time off directly in Slack or Teams, managers approve or reject in one click, and the team calendar and statuses sync automatically. Free under 10 users, from $1/user/month after that, and setup takes about 3 minutes.',
  ],
  [
    "Can employees see each other's balances?",
    'No, balances are private. The team calendar only shows the dates people are off, never how many days anyone has left.',
  ],
  [
    'How does accrual math work?',
    "It's fully automated and customizable to everyone's needs. Define your quota and accrual rules per team, and Palmy tracks balances over time for you.",
  ],
  [
    'Does Palmy sync with our calendar?',
    'Yes. Approved time off syncs automatically to your company-wide Google Calendar or Outlook, and Slack or Teams statuses flip to 🌴 while people are out.',
  ],
  [
    'Does Palmy work with Microsoft Teams?',
    'Yes. Request time off, approve requests and see who is off from Slack, Microsoft Teams or the web dashboard.',
  ],
  [
    'Can we set different policies per team?',
    'Yes. Unlimited, accrued or regional: mix and match policies per team, and personalize the vacation types to fit how your company works.',
  ],
];

export function PalmyFaq() {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <section className="bh-section" style={{ background: 'var(--bh-cream)' }}>
      <style>{`.palmy-faq[open] .palmy-faq-plus{transform:rotate(45deg)}`}</style>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em', color: 'var(--bh-ink-mute)', textTransform: 'uppercase', marginBottom: 10 }}>
          Frequently asked questions
        </div>
        <h2 style={{ fontSize: 'clamp(36px, 4.4vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.02, marginBottom: 36 }}>Before you ask.</h2>
        <div>
          {FAQS.map(([q, a], i) => (
            <details key={i} className="palmy-faq" style={{ borderBottom: '1px solid var(--bh-line)', padding: '20px 0' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  listStyle: 'none',
                  fontFamily: 'var(--bh-font-display)',
                  fontSize: 21,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: 'var(--bh-ink)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                {q}
                <span className="palmy-faq-plus" style={{ color: ACCENT, fontSize: 28, lineHeight: 1, flexShrink: 0, transition: 'transform .2s ease' }}>
                  +
                </span>
              </summary>
              <p style={{ fontSize: 16, color: 'var(--bh-ink-soft)', lineHeight: 1.6, marginTop: 14 }}>{a}</p>
            </details>
          ))}
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// FINAL CTA (dark)
// ════════════════════════════════════════════════════════════════
export function PalmyFinalCTA({ app }) {
  return (
    <section
      className="bh-section"
      style={{ background: 'var(--bh-ink)', color: 'var(--bh-cream)', position: 'relative', overflow: 'hidden' }}
    >
      <div style={{ maxWidth: 920, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div className="bh-float" style={{ width: 110, height: 110, margin: '0 auto 20px' }}>
          <Image src={LOGO} alt="Palmy logo" width={110} height={110} />
        </div>
        <h2 style={{ fontSize: 'clamp(40px, 5vw, 64px)', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 18, color: 'var(--bh-cream)' }}>
          Ready to drop the vacation <span style={{ fontStyle: 'italic', color: ACCENT }}>hassle</span>?
        </h2>
        <p style={{ fontSize: 18, color: 'rgba(255,248,238,0.72)', lineHeight: 1.5, maxWidth: 540, margin: '0 auto 30px' }}>
          Free under 10 users, forever. $1/user/month after that. No card, no sales call. Just add it and let time off run
          itself.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <AddToSlackButton app={app} text="Add Palmy to Slack" bhVariant="primary" />
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// CROSS-SELL — "Palmy plays nice with all seven of its friends."
// Thin wrapper over the shared AppsCrossSell (components/appsCrossSell.js).
// ════════════════════════════════════════════════════════════════
export function PalmyCrossSell() {
  return <AppsCrossSell currentApp="palmy" accent={ACCENT} />;
}
